import type { BaseSyntheticEvent, HTMLAttributes, ReactNode } from "react";

import { useUser } from "@auth0/nextjs-auth0";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import clsx from "clsx";

import { robotoCondensed } from "@/fonts";
import { LoginButton } from "@/src/components/navigation/LoginButton";
import { LogoutButton } from "@/src/components/navigation/LogoutButton";
import { NavigationItemsConsumer } from "@/src/components/navigation/NavigationItemsConsumer";
import { NavigationItemsSocietaire } from "@/src/components/navigation/NavigationItemsSocietaire";
import { Button } from "@/src/components/ui/Button/Button";

export interface NavigationItem {
  href: string;
  icon?: string;
  label: string;
}

interface NavigationItemsProps extends HTMLAttributes<HTMLDivElement> {
  readonly consumerItems: NavigationItem[];
  readonly opened: boolean;
  readonly societaireItems: NavigationItem[];
}

// TODO(legacy-nav): 230px mirrors the auth button on the legacy site
// (cestquilepatron.com), which shares this header. Drop back to the Tailwind
// scale (w-52) once that site is retired.
const authButtonWidth = "w-[230px]";

export function NavigationItems({
  consumerItems,
  opened,
  societaireItems,
}: NavigationItemsProps): ReactNode {
  const { user } = useUser();

  return (
    <>
      <NavigationItemsConsumer
        className={clsx("hidden", "md:flex")}
        items={consumerItems}
      />

      <NavigationMenu.Item className={clsx("list-none", "md:hidden")}>
        <NavigationMenu.Trigger
          asChild
          onPointerLeave={preventDefault}
          onPointerMove={preventDefault}
        >
          <button
            aria-label={opened ? "Fermer le menu" : "Ouvrir le menu"}
            className={clsx(
              `group flex cursor-pointer items-center self-stretch overflow-hidden rounded-bl px-3 py-2.5`,
              "focus-visible:outline-hidden",
              "hover:bg-primary-hover",
            )}
            type="button"
          >
            <span className="relative block size-9">
              <XMarkIcon
                className={clsx(
                  "absolute inset-0 size-9 transition-opacity duration-200 ease-in-out",
                  { "opacity-0": !opened },
                )}
                strokeWidth={2}
              />
              <Bars3Icon
                className={clsx(
                  "absolute inset-0 size-9 transition-opacity duration-200 ease-in-out",
                  { "opacity-0": opened },
                )}
                strokeWidth={2}
              />
            </span>
          </button>
        </NavigationMenu.Trigger>
        <NavigationMenu.Content
          className="bg-primary absolute top-14 right-0 flex max-h-[calc(100vh-3.5rem)] w-screen flex-col items-center gap-5 overflow-y-auto p-8 text-white shadow-2xl transition duration-200 ease-out"
          data-testid="burger-menu-small-screen"
          onPointerEnter={preventDefault}
          onPointerLeave={preventDefault}
        >
          <NavigationItemsConsumer items={consumerItems} />
          {user ? (
            <>
              <div className="w-full max-w-80 rounded-sm bg-white p-6">
                <h2
                  className={clsx(
                    "text-primary mb-4 text-xl tracking-wide",
                    robotoCondensed.className,
                  )}
                >
                  Espace sociétaire
                </h2>
                <NavigationItemsSocietaire items={societaireItems} />
              </div>
              <LogoutButton />
            </>
          ) : (
            <LoginButton className="mb-2" />
          )}
        </NavigationMenu.Content>
      </NavigationMenu.Item>

      {user ? (
        <NavigationMenu.Item className={clsx("relative hidden", "md:block")}>
          <NavigationMenu.Trigger
            asChild
            onPointerLeave={preventDefault}
            onPointerMove={preventDefault}
          >
            <div>
              <Button
                aria-haspopup="true"
                className={clsx(`mx-3 ${authButtonWidth}`, "md:block")}
                darkBackground
                size="medium"
              >
                Espace sociétaire
              </Button>
            </div>
          </NavigationMenu.Trigger>
          <NavigationMenu.Content
            className="bg-primary absolute right-0 max-h-[calc(100vh-3.5rem)] w-80 overflow-y-auto rounded-sm p-5 text-white shadow-xl transition duration-200 ease-out"
            data-testid="societaire-menu-large-screen"
            onPointerEnter={preventDefault}
            onPointerLeave={preventDefault}
          >
            <NavigationItemsSocietaire
              className="mb-5 rounded-sm bg-white p-6"
              items={societaireItems}
            />
            <LogoutButton />
          </NavigationMenu.Content>
        </NavigationMenu.Item>
      ) : (
        <LoginButton
          className={clsx(`mx-3 hidden ${authButtonWidth}`, "md:block")}
        />
      )}
    </>
  );
}

function preventDefault(event: BaseSyntheticEvent): void {
  event.preventDefault();
}
